import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { Zap, ArrowLeft, Check, Loader2, Tag, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID || "sb"; // sandbox for testing

// Base prices
const PRICES = {
  single: 14.99,
  subscription: 49.99,
  extra: 9.99
};

export default function Pricing() {
  const { t } = useTranslation();
  const { user, token, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  
  // Discount code state
  const [discountCode, setDiscountCode] = useState("");
  const [discountLoading, setDiscountLoading] = useState(false);
  const [appliedDiscount, setAppliedDiscount] = useState(null);

  useEffect(() => {
    if (user) {
      fetchPaymentStatus();
    }
  }, [user]);

  const fetchPaymentStatus = async () => {
    try {
      const response = await fetch(`${API}/payment/status`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (response.ok) {
        const data = await response.json();
        setPaymentStatus(data);
      }
    } catch (error) {
      console.error("Error fetching payment status:", error);
    }
  };

  const validateDiscountCode = async () => {
    if (!discountCode.trim()) {
      toast.error("Bitte gib einen Code ein");
      return;
    }

    setDiscountLoading(true);
    try {
      const response = await fetch(`${API}/discount/validate?code=${encodeURIComponent(discountCode)}&payment_type=${selectedPlan || 'all'}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" }
      });

      if (response.ok) {
        const data = await response.json();
        setAppliedDiscount(data);
        toast.success(`Code "${data.code}" angewendet: ${data.discount_percent}% Rabatt!`);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Ungültiger Code");
        setAppliedDiscount(null);
      }
    } catch (error) {
      toast.error("Fehler bei der Code-Validierung");
    } finally {
      setDiscountLoading(false);
    }
  };

  const removeDiscount = () => {
    setAppliedDiscount(null);
    setDiscountCode("");
  };

  const getDiscountedPrice = (plan) => {
    const basePrice = PRICES[plan];
    if (!appliedDiscount) return basePrice;
    
    // Check if discount applies to this plan
    if (appliedDiscount.applies_to !== "all" && appliedDiscount.applies_to !== plan) {
      return basePrice;
    }
    
    const discount = basePrice * (appliedDiscount.discount_percent / 100);
    return Math.round((basePrice - discount) * 100) / 100;
  };

  const createOrder = async (data, actions) => {
    let amount, description;
    const discountedPrice = getDiscountedPrice(selectedPlan);
    
    if (selectedPlan === "single") {
      amount = discountedPrice.toFixed(2);
      description = appliedDiscount 
        ? `VeloMetrics Einzelanalyse (${appliedDiscount.discount_percent}% Rabatt)`
        : "VeloMetrics Einzelanalyse";
    } else if (selectedPlan === "subscription") {
      amount = discountedPrice.toFixed(2);
      description = appliedDiscount 
        ? `VeloMetrics Monatsabo - 1. Monat (${appliedDiscount.discount_percent}% Rabatt)`
        : "VeloMetrics Monatsabo (5 Analysen)";
    } else if (selectedPlan === "extra") {
      amount = discountedPrice.toFixed(2);
      description = "VeloMetrics Zusätzliche Analyse";
    }

    return actions.order.create({
      purchase_units: [
        {
          amount: {
            value: amount,
            currency_code: "EUR"
          },
          description: description
        }
      ]
    });
  };

  const onApprove = async (data, actions) => {
    setProcessing(true);
    try {
      await actions.order.capture();
      
      // Mark discount code as used if applied
      if (appliedDiscount) {
        await fetch(`${API}/discount/use?code=${encodeURIComponent(appliedDiscount.code)}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token && { Authorization: `Bearer ${token}` })
          },
          credentials: "include"
        });
      }
      
      // Record payment in backend
      const response = await fetch(`${API}/payment/capture`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` })
        },
        credentials: "include",
        body: JSON.stringify({
          payment_type: selectedPlan,
          order_id: data.orderID,
          discount_code: appliedDiscount?.code || null
        })
      });

      if (response.ok) {
        toast.success("Zahlung erfolgreich! Du kannst jetzt Analysen durchführen.");
        await checkAuth();
        navigate("/upload");
      } else {
        toast.error("Fehler bei der Zahlungsverarbeitung");
      }
    } catch (error) {
      toast.error("Zahlung fehlgeschlagen");
    } finally {
      setProcessing(false);
      setSelectedPlan(null);
      setAppliedDiscount(null);
      setDiscountCode("");
    }
  };

  const onError = (err) => {
    console.error("PayPal error:", err);
    toast.error("PayPal-Fehler. Bitte versuche es erneut.");
    setSelectedPlan(null);
  };

  const handleSelectPlan = (plan) => {
    if (!user) {
      toast.error("Bitte melde dich zuerst an");
      navigate("/login", { state: { from: { pathname: "/pricing" } } });
      return;
    }
    setSelectedPlan(plan);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-lg border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14 sm:h-16">
            <Link to="/" className="flex items-center gap-2">
              <Zap className="h-6 w-6 sm:h-8 sm:w-8 text-volt" />
              <span className="font-display font-bold text-lg sm:text-xl tracking-tight">VELOMETRICS</span>
              <span className="text-xs text-muted-foreground hidden md:inline">by Loops Cycling Academy</span>
            </Link>
            <div className="flex items-center gap-2 sm:gap-4">
              <LanguageSwitcher variant="minimal" />
              <Link to={user ? "/dashboard" : "/"} className="nav-link text-xs sm:text-sm font-medium flex items-center gap-1 sm:gap-2">
                <ArrowLeft className="h-4 w-4" />
                <span className="hidden sm:inline">{t('common.back')}</span>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-18 sm:pt-24 pb-8 sm:pb-12 px-3 sm:px-6 lg:px-8" data-testid="pricing-page">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <p className="data-label mb-2 sm:mb-4 text-xs sm:text-sm">{t('nav.pricing')}</p>
            <h1 className="font-display text-2xl sm:text-4xl md:text-5xl font-bold tracking-tight uppercase mb-3 sm:mb-4">
              {t('pricing.title').split(' ').slice(0, -1).join(' ')} <span className="text-volt">{t('pricing.title').split(' ').slice(-1)}</span>
            </h1>
            <p className="text-sm sm:text-lg text-muted-foreground px-2">
              {t('pricing.subtitle')}
            </p>
          </div>

          {/* Current Status */}
          {paymentStatus?.is_active && (
            <div className="glass-card rounded-xl p-4 sm:p-6 mb-6 sm:mb-8 border border-volt/30">
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-volt/20 flex items-center justify-center flex-shrink-0">
                  <Check className="h-5 w-5 sm:h-6 sm:w-6 text-volt" />
                </div>
                <div className="min-w-0">
                  <p className="font-display font-bold uppercase text-sm sm:text-base">{t('pricing.activeSubscription')}</p>
                  <p className="text-xs sm:text-sm text-muted-foreground truncate">
                    {paymentStatus.subscription_type === "subscription" 
                      ? `${t('pricing.subscription.title')} - ${paymentStatus.analyses_remaining || 0} ${t('pricing.analysesRemaining')}` 
                      : `${t('pricing.single.title')} - ${paymentStatus.analyses_remaining || 0} ${t('pricing.analysesRemaining')}`}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Pricing Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-8">
            {/* Single Analysis */}
            <div className={`glass-card rounded-xl p-5 sm:p-8 relative overflow-hidden transition-all ${
              selectedPlan === "single" ? "border-2 border-volt" : ""
            }`}>
              <div className="absolute top-0 right-0 w-32 h-32 bg-volt/10 rounded-full blur-3xl"></div>
              
              <p className="data-label mb-1 sm:mb-2 text-xs sm:text-sm">{t('pricing.single.title')}</p>
              <div className="flex items-end gap-1 mb-2">
                <span className="number-display text-3xl sm:text-5xl text-volt">{t('pricing.single.price')}</span>
                <span className="text-base sm:text-xl text-muted-foreground mb-1 sm:mb-2">{t('pricing.single.currency')}</span>
              </div>
              <p className="text-xs sm:text-sm text-cyan font-medium mb-3 sm:mb-4">
                {t('pricing.single.subtitle')}
              </p>
              <p className="text-xs sm:text-base text-muted-foreground mb-3 sm:mb-4">
                {t('pricing.single.desc')}
              </p>
              <p className="text-xs sm:text-sm text-volt/80 mb-4 sm:mb-6 border-l-2 border-volt/50 pl-3">
                {t('pricing.single.highlight')}
              </p>
              
              <ul className="space-y-2 sm:space-y-3 mb-6 sm:mb-8">
                {[
                  t('pricing.features.singleAnalysis'),
                  t('pricing.features.allZones'),
                  t('pricing.features.vo2maxCalc'),
                  t('pricing.features.thresholds'),
                  t('pricing.features.carbConsumption'),
                  t('pricing.features.pdfReport'),
                  t('pricing.features.peerComparison')
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm">
                    <Check className="h-4 w-4 text-volt flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>

              {selectedPlan === "single" ? (
                <div className="space-y-4">
                  {/* Discount Code Input */}
                  <div className="p-4 rounded-lg bg-white/5 border border-white/10">
                    <div className="flex items-center gap-2 mb-2">
                      <Tag className="h-4 w-4 text-carb-orange" />
                      <span className="text-sm font-medium">{t('pricing.discountCode')}</span>
                    </div>
                    {appliedDiscount ? (
                      <div className="flex items-center justify-between p-2 rounded bg-green-500/10 border border-green-500/30">
                        <div>
                          <span className="font-mono font-bold text-green-400">{appliedDiscount.code}</span>
                          <span className="ml-2 text-green-400">-{appliedDiscount.discount_percent}%</span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={removeDiscount}
                          className="h-8 w-8 p-0 hover:bg-red-500/20"
                        >
                          <X className="h-4 w-4 text-red-400" />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <Input
                          value={discountCode}
                          onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                          placeholder={t('pricing.enterCode')}
                          className="bg-white/5 border-white/10 font-mono uppercase"
                          data-testid="discount-input-single"
                        />
                        <Button
                          onClick={validateDiscountCode}
                          disabled={discountLoading || !discountCode}
                          className="bg-carb-orange text-black hover:bg-carb-orange/90"
                        >
                          {discountLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : t('pricing.apply')}
                        </Button>
                      </div>
                    )}
                  </div>
                  
                  {/* Price Display */}
                  {appliedDiscount && (
                    <div className="text-center p-3 rounded-lg bg-volt/10 border border-volt/30">
                      <span className="text-muted-foreground line-through mr-2">{PRICES.single.toFixed(2)}€</span>
                      <span className="text-2xl font-bold text-volt">{getDiscountedPrice("single").toFixed(2)}€</span>
                    </div>
                  )}
                  
                  <PayPalScriptProvider options={{ 
                    clientId: PAYPAL_CLIENT_ID, 
                    currency: "EUR",
                    intent: "capture"
                  }}>
                    <PayPalButtons
                      style={{ layout: "vertical", color: "gold", shape: "rect" }}
                      createOrder={createOrder}
                      onApprove={onApprove}
                      onError={onError}
                      disabled={processing}
                    />
                  </PayPalScriptProvider>
                </div>
              ) : (
                <Button
                  onClick={() => handleSelectPlan("single")}
                  variant="outline"
                  className="w-full border-white/20 hover:bg-white/10 uppercase tracking-wider h-12"
                  data-testid="select-single-btn"
                >
                  {t('pricing.select')}
                </Button>
              )}
            </div>

            {/* Subscription */}
            <div className={`glass-card rounded-xl p-8 relative overflow-hidden transition-all ${
              selectedPlan === "subscription" ? "border-2 border-volt" : "border border-volt/30"
            }`}>
              <div className="absolute top-0 right-0 w-32 h-32 bg-volt/20 rounded-full blur-3xl"></div>
              <div className="absolute top-4 right-4">
                <span className="bg-volt text-black text-xs font-bold px-3 py-1 rounded-full uppercase">
                  {t('pricing.subscription.badge')}
                </span>
              </div>
              
              <p className="data-label mb-2">{t('pricing.subscription.title')}</p>
              <div className="flex items-end gap-1 mb-2">
                <span className="number-display text-5xl text-volt">{t('pricing.subscription.price')}</span>
                <span className="text-xl text-muted-foreground mb-2">{t('pricing.subscription.currency')}</span>
              </div>
              <p className="text-sm text-cyan font-medium mb-4">
                {t('pricing.subscription.subtitle')}
              </p>
              <p className="text-muted-foreground mb-4">
                {t('pricing.subscription.desc')}
              </p>
              <p className="text-sm text-volt/80 mb-6 border-l-2 border-volt/50 pl-3">
                {t('pricing.subscription.highlight')}
              </p>
              
              <ul className="space-y-3 mb-8">
                {[
                  t('pricing.features.fiveAnalyses'),
                  t('pricing.features.additionalAnalyses'),
                  t('pricing.features.allZones'),
                  t('pricing.features.vo2maxCalc'),
                  t('pricing.features.thresholds'),
                  t('pricing.features.carbConsumption'),
                  t('pricing.features.pdfReports'),
                  t('pricing.features.peerComparison'),
                  t('pricing.features.cancelMonthly')
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm">
                    <Check className="h-4 w-4 text-volt flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>

              {selectedPlan === "subscription" ? (
                <div className="space-y-4">
                  {/* Discount Code Input */}
                  <div className="p-4 rounded-lg bg-white/5 border border-white/10">
                    <div className="flex items-center gap-2 mb-2">
                      <Tag className="h-4 w-4 text-carb-orange" />
                      <span className="text-sm font-medium">{t('pricing.discountCode')}</span>
                      <span className="text-xs text-muted-foreground">{t('pricing.discountFirstMonth')}</span>
                    </div>
                    {appliedDiscount ? (
                      <div className="flex items-center justify-between p-2 rounded bg-green-500/10 border border-green-500/30">
                        <div>
                          <span className="font-mono font-bold text-green-400">{appliedDiscount.code}</span>
                          <span className="ml-2 text-green-400">-{appliedDiscount.discount_percent}%</span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={removeDiscount}
                          className="h-8 w-8 p-0 hover:bg-red-500/20"
                        >
                          <X className="h-4 w-4 text-red-400" />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <Input
                          value={discountCode}
                          onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                          placeholder={t('pricing.enterCode')}
                          className="bg-white/5 border-white/10 font-mono uppercase"
                          data-testid="discount-input-subscription"
                        />
                        <Button
                          onClick={validateDiscountCode}
                          disabled={discountLoading || !discountCode}
                          className="bg-carb-orange text-black hover:bg-carb-orange/90"
                        >
                          {discountLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : t('pricing.apply')}
                        </Button>
                      </div>
                    )}
                  </div>
                  
                  {/* Price Display */}
                  {appliedDiscount && (
                    <div className="text-center p-3 rounded-lg bg-volt/10 border border-volt/30">
                      <div className="text-xs text-muted-foreground mb-1">{t('pricing.firstMonth')}:</div>
                      <span className="text-muted-foreground line-through mr-2">{PRICES.subscription.toFixed(2)}€</span>
                      <span className="text-2xl font-bold text-volt">{getDiscountedPrice("subscription").toFixed(2)}€</span>
                      <div className="text-xs text-muted-foreground mt-1">{t('pricing.afterThat')}: {PRICES.subscription.toFixed(2)}€{t('pricing.perMonth')}</div>
                    </div>
                  )}
                  
                  <PayPalScriptProvider options={{ 
                    clientId: PAYPAL_CLIENT_ID, 
                    currency: "EUR",
                    intent: "capture"
                  }}>
                    <PayPalButtons
                      style={{ layout: "vertical", color: "gold", shape: "rect" }}
                      createOrder={createOrder}
                      onApprove={onApprove}
                      onError={onError}
                      disabled={processing}
                    />
                  </PayPalScriptProvider>
                </div>
              ) : (
                <Button
                  onClick={() => handleSelectPlan("subscription")}
                  className="w-full skew-btn bg-volt text-black hover:bg-volt/90 font-bold uppercase tracking-wider h-12"
                  data-testid="select-subscription-btn"
                >
                  <span>{t('pricing.select')}</span>
                </Button>
              )}
            </div>
          </div>

          {/* Extra Analysis Card for Subscribers */}
          {paymentStatus?.is_active && paymentStatus?.subscription_type === "subscription" && paymentStatus?.analyses_remaining === 0 && (
            <div className="mt-8 glass-card rounded-xl p-6 border border-carb-orange/30">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div>
                  <h3 className="font-display font-bold uppercase">{t('pricing.extraAnalysis.title')}</h3>
                  <p className="text-sm text-muted-foreground">{t('pricing.extraAnalysis.desc')}</p>
                </div>
                <Button
                  onClick={() => handleSelectPlan("extra")}
                  className="skew-btn bg-carb-orange text-black hover:bg-carb-orange/90 font-bold uppercase tracking-wider"
                  data-testid="buy-extra-btn"
                >
                  <span>{t('pricing.extraAnalysis.cta')}</span>
                </Button>
              </div>
            </div>
          )}

          {/* Processing Overlay */}
          {processing && (
            <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
              <div className="glass-card rounded-xl p-8 text-center">
                <Loader2 className="h-12 w-12 text-volt animate-spin mx-auto mb-4" />
                <p className="font-display font-bold uppercase">{t('pricing.processing')}</p>
              </div>
            </div>
          )}

          {/* FAQ */}
          <div className="mt-16">
            <h2 className="font-display text-2xl font-bold uppercase text-center mb-8">
              {t('pricing.faq.title')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                { q: t('pricing.faq.q1'), a: t('pricing.faq.a1') },
                { q: t('pricing.faq.q2'), a: t('pricing.faq.a2') },
                { q: t('pricing.faq.q3'), a: t('pricing.faq.a3') },
                { q: t('pricing.faq.q4'), a: t('pricing.faq.a4') }
              ].map((faq, i) => (
                <div key={i} className="glass-card rounded-xl p-6">
                  <h3 className="font-medium mb-2">{faq.q}</h3>
                  <p className="text-sm text-muted-foreground">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
